/**
 * A message's notification: who and where in the title, the text with its
 * mentions spelled out as names, the sender's avatar, and the route to the
 * message itself.
 */
import { ChannelType, type Message } from '@neosocial/protocol';
import { tr } from '../i18n';
import { assetUrl } from '../lib/config';
import { useData } from '../store/data';
import type { DataState } from '../store/reducer';
import { displayName, privateChannelName } from '../store/selectors';
import type { NotifyInput, NotifyKind } from './notify';

const BODY_MAX = 200;

/** The text as people read it: `<@1>` as @name, `<#2>` as #channel, `<:x:3>` as :x:. */
export function plainMessageText(s: DataState, m: Message): string {
  const guildId = s.channels[m.channel_id]?.guild_id ?? null;
  // a forward says what it forwards, a poll its question (polls and forwarding)
  const raw = m.content || m.message_snapshots?.[0]?.message.content || m.poll?.question.text || '';
  const text = raw
    .replace(/<@!?(\d{1,20})>/g, (_, id: string) => {
      const u = s.users[id] ?? m.mentions.find((x) => x.id === id);
      return '@' + (u ? displayName(u, guildId ? s.members[guildId]?.[id] : null) : '…');
    })
    .replace(/<@&(\d{1,20})>/g, (_, id: string) => {
      const r = guildId ? s.guilds[guildId]?.roles?.find((x) => x.id === id) : undefined;
      return '@' + (r?.name ?? '…');
    })
    .replace(/<#(\d{1,20})>/g, (_, id: string) => '#' + (s.channels[id]?.name ?? '…'))
    .replace(/<a?:([\w~]{1,32}):\d{1,20}>/g, ':$1:')
    .replace(/\s+/g, ' ')
    .trim();
  if (text) return text.length > BODY_MAX ? text.slice(0, BODY_MAX - 1) + '…' : text;
  return m.attachments.length ? tr().t('nt.attachment') : '';
}

/** The notification for `m`, already judged worth one as `kind`. */
export function messageNotification(m: Message, kind: NotifyKind): NotifyInput {
  const s = useData.getState();
  const { t } = tr();
  const c = s.channels[m.channel_id];
  const guildId = c?.guild_id ?? null;
  const who = displayName(
    s.users[m.author.id] ?? m.author,
    guildId ? s.members[guildId]?.[m.author.id] : null,
  );
  const text = plainMessageText(s, m);
  const priv = c?.type === ChannelType.DM || c?.type === ChannelType.GROUP_DM;
  let title: string;
  let body = text;
  if (c?.type === ChannelType.GROUP_DM) {
    title = privateChannelName(s, c);
    body = text ? `${who}: ${text}` : who;
  } else if (priv) {
    title = who;
  } else {
    const channel = c?.name ?? '';
    title =
      kind === 'reply' ? t('nt.replyIn', { who, channel }) : t('nt.inChannel', { who, channel });
  }
  return {
    kind,
    title,
    body,
    who,
    from: m.author.id,
    icon: assetUrl(m.author.avatar ?? null),
    route: { to: priv ? 'dm' : 'channel', channelId: m.channel_id, messageId: m.id },
  };
}
